export const enum sourceTypesEnum {
  ERROR = "ERROR",
  FACEBOOK = "FACEBOOK",
  TWITTER = "TWITTER",
  INSTAGRAM = "INSTAGRAM",
  ICAL = "ICAL",
  PINTEREST = "PINTEREST",
  YELP = "YELP",
  YOUTUBE = "YOUTUBE",
  VIMEO = "VIMEO",
  RSS = "RSS",
  GOOGLE_PLACES = "GOOGLE_PLACES",
  GOOGLE_DRIVE = "GOOGLE_DRIVE",
  CUSTOM = "CUSTOM",
  O365 = "O365",
  SERP_API = "SERP_API",
  PARENTSQUARE = "PARENTSQUARE",
}

export type ContentItemTypes =
  | "is_news"
  | "is_media"
  | "is_event"
  | "is_file"
  | "is_folder"
  | "is_description"
  | "is_location"
  | "is_review"
  | "is_quicklink";

export interface sourceType {
  type: sourceTypesEnum;
  title: string;
  feed_name: string;
  placeholder_url: string | boolean;
  items_name: string;
  icons: {
    fontawesome: string;
    flaticon: string;
  };
  contentTypes: Array<ContentItemTypes>;
  phone?: boolean;
}

class SourceUtils {
  public sourceTypes: sourceType[];
  public blockTypes;
  public sourceTitles;
  public sourceFlaticons;
  public sourceFontawesomeIcons;
  public socialSourceTypes;
  public sourceFeedNames;

  constructor() {
    // this holds all the default data for each source
    this.sourceTypes = [
      {
        type: sourceTypesEnum.ERROR,
        title: "Error",
        feed_name: "Errored Source",
        placeholder_url: false,
        items_name: "errored items",
        icons: {
          fontawesome: "fas fa-exclamation-triangle",
          flaticon: "flaticon-error-1",
        },
        contentTypes: [],
      },
      {
        type: sourceTypesEnum.FACEBOOK,
        title: "Facebook",
        feed_name: "Facebook Feed",
        placeholder_url: "facebook.com/example",
        items_name: "facebook items",
        icons: {
          fontawesome: "fab fa-facebook",
          flaticon: "flaticon-facebook",
        },
        contentTypes: ["is_news", "is_media"],
      },
      {
        type: sourceTypesEnum.ICAL,
        title: "Calendar",
        feed_name: "Calendar Feed",
        placeholder_url: "enter a public ics url",
        items_name: "events",
        icons: {
          fontawesome: "fas fa-calendar-alt",
          flaticon: "flaticon-calendar-2",
        },
        contentTypes: ["is_event"],
      },
      {
        type: sourceTypesEnum.PINTEREST,
        title: "Pinterest",
        feed_name: "Pinterest Feed",
        placeholder_url: "pinterest.com/example",
        items_name: "pins",
        icons: {
          fontawesome: "fab fa-pinterest",
          flaticon: "flaticon-pinterest-logo",
        },
        contentTypes: ["is_media"],
      },
      {
        type: sourceTypesEnum.YOUTUBE,
        title: "Youtube",
        feed_name: "Youtube Feed",
        placeholder_url: "youtube.com/user/example",
        items_name: "youtube videos",
        icons: {
          fontawesome: "fab fa-youtube",
          flaticon: "flaticon-youtube",
        },
        contentTypes: ["is_media"],
      },
      {
        type: sourceTypesEnum.VIMEO,
        title: "Vimeo",
        feed_name: "Vimeo Feed",
        placeholder_url: "vimeo.com/example",
        items_name: "vimeo videos",
        icons: {
          fontawesome: "fab fa-vimeo-v",
          flaticon: "flaticon-vimeo-square-logo",
        },
        contentTypes: ["is_media"],
      },
      {
        type: sourceTypesEnum.RSS,
        title: "RSS",
        feed_name: "RSS Feed",
        placeholder_url: "enter a public rss url",
        items_name: "rss feed items",
        icons: {
          fontawesome: "fas fa-rss-square",
          flaticon: "flaticon-rss-feed-button",
        },
        contentTypes: ["is_news"],
      },
      {
        type: sourceTypesEnum.GOOGLE_DRIVE,
        title: "Google Drive",
        feed_name: "Google Drive Source",
        phone: false,
        placeholder_url: "Enter a public file/folder url",
        items_name: "google drive files and folders",
        icons: {
          fontawesome: "fab fa-google-drive",
          flaticon: "flaticon-google-drive-logo",
        },
        contentTypes: ["is_file", "is_folder"],
      },
      {
        type: sourceTypesEnum.O365,
        title: "Office 365",
        feed_name: "Office 365 Source",
        phone: false,
        placeholder_url: "Enter a public file/folder url",
        items_name: "office 365 files and folders",
        icons: {
          fontawesome: "fab fa-microsoft",
          flaticon: "flaticon-microsoft",
        },
        contentTypes: ["is_file", "is_folder"],
      },
      {
        type: sourceTypesEnum.CUSTOM,
        title: "Custom",
        feed_name: "Custom Feed",
        placeholder_url: "",
        items_name: "custom items",
        icons: {
          fontawesome: "",
          flaticon: "",
        },
        contentTypes: [],
      },
      {
        type: sourceTypesEnum.PARENTSQUARE,
        title: "ParentSquare",
        feed_name: "ParentSquare Feed",
        placeholder_url: "parentsquare.com/districts/example/rss_widget",
        items_name: "parentsquare posts",
        icons: {
          fontawesome: "sbicon-parentsqaure",
          flaticon: "sbicon-parentsqaure",
        },
        contentTypes: ["is_news"],
      },
      {
        type: sourceTypesEnum.SERP_API,
        title: "",
        feed_name: "News Feed",
        placeholder_url: "",
        items_name: "news",
        icons: {
          fontawesome: 'fas fa-newspaper"',
          flaticon: "flaticon-newspaper",
        },
        contentTypes: ["is_news", "is_event"],
      },
    ];

    this.blockTypes = {};
    this.sourceTitles = {};
    this.sourceFlaticons = {};
    this.sourceFontawesomeIcons = {};
    this.sourceFeedNames = {};
    this.socialSourceTypes = [];

    this.sourceTypes.forEach((source) => {
      source.contentTypes.forEach((block) => {
        if (this.blockTypes[block]) {
          this.blockTypes[block].push(source.type);
        } else {
          this.blockTypes[block] = [source.type];
        }
      });

      this.sourceTitles[source.type] = source.title;
      this.sourceFlaticons[source.type] = source.icons.flaticon;
      this.sourceFontawesomeIcons[source.type] = source.icons.fontawesome;
      this.sourceFeedNames[source.type] = source.feed_name;

      if (
        source.type !== sourceTypesEnum.ERROR &&
        source.type !== sourceTypesEnum.CUSTOM
      ) {
        this.socialSourceTypes.push(source.type);
      }
    });
  }
}

const sourceUtils = new SourceUtils();

export const sourceTypes = sourceUtils.sourceTypes;

export const sourceFlaticons = sourceUtils.sourceFlaticons;
export const sourceFontawesomeIcons = sourceUtils.sourceFontawesomeIcons;
export const sourceTitles = sourceUtils.sourceTitles;
export const socialSourceTypes = sourceUtils.socialSourceTypes;
export const sourceFeedNames = sourceUtils.sourceFeedNames;
